import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  ExternalCommissioningRequestModel,
  ImSysCommissioningRequestForm
} from '@bo-client/activation-management/shared/models/ExternalCommissioningRequestModel';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  ActionTypeEnum,
  AreaTypeEnum,
  CommissioningPowerMeterType
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { GisDataEvent } from '@bo-client/shared/components/get-gis-data/get-gis-data.component';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import {
  AnswerWithMoreInfoModel,
  NavigateToEnum
} from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { path } from '@bo-client/shared/constants/path';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import {
  DeviceTypeName,
  MobileConnectionStrengthDisplayNames,
  MobileConnectionStrengthEnum
} from '@bo-client/activation-management/shared/commissioning-constans';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { combineLatestWith, map, shareReplay, startWith } from 'rxjs/operators';
import { uri } from '@bo-client/shared/constants/uri';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { DocumentTagEnum, FieldConfigEnum, UserDecisionEnum } from '@bo-client/shared/constants/enums';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PowerCommissioningConfig } from '@bo-client/shared/config.model';
import { requireDocumentsValidator } from '@bo-client/shared/forms/forms.validators';

@Component({
  selector: 'app-imsys-installation-page',
  templateUrl: './imsys-installation-page.component.html',
  styleUrls: ['./imsys-installation-page.component.scss']
})
export class ImsysInstallationPageComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private createFormsService = inject(CreateFormsService);
  private httpHapService = inject(HttpHapService);
  private toastrService = inject(ToastrService);
  private router = inject(Router);
  private dataShareService = inject(DataShareService);
  private destroyRef = inject(DestroyRef);

  powerConfig$: Observable<PowerCommissioningConfig>;

  imSysForm$: Observable<ImSysCommissioningRequestForm>;

  selectedConnectionObject$: BehaviorSubject<GisDataModel> = new BehaviorSubject(null);
  companyLocationsByTenant$: Observable<CompanyLocationDto[]>;
  eventsFromParent$ = of('');

  deviceTypeOptions = Object.values(DeviceTypeName).filter((value) => value !== DeviceTypeName.Erzeuger);

  mobileConnectionStrengthOptions = Object.values(MobileConnectionStrengthEnum);

  ngOnInit() {
    this.powerConfig$ = this.dataShareService.getSingleTenantConfig$<PowerCommissioningConfig>(
      'commissioning.powerConfig',
      {}
    );
    this.companyLocationsByTenant$ = this.httpHapService.getTyped<CompanyLocationDto[]>(
      uri.companyLocationByCurrentTenant
    );
    this.imSysForm$ = this.dataShareService.uiAdaptions$.pipe(
      takeUntilDestroyed(this.destroyRef),
      combineLatestWith(this.powerConfig$),
      map(([adaptions, config]) => {
        const {
          externalImsysInstallationUploadsField,
          externalImsysInstallationIsFeederPresentField,
          externalImsysInstallationFeederTypeField,
          externalImsysInstallationIsConsumerPresentField,
          externalImsysInstallationConsumerTypeField,
          externalImsysInstallationIsAnnualConsumptionExceededField,
          externalImsysInstallationMeterNumberField,
          externalImsysInstallationDeviceTypeField,
          externalImsysInstallationMobileConnectionStrengthField
        } = config;
        const pricingTableAdaption = adaptions.find(
          (adaption) => adaption.anchor === 'commissioning.external.pricingTableConditions'
        );
        const adaptionVisible =
          pricingTableAdaption &&
          (pricingTableAdaption.visibility === 'show' || pricingTableAdaption.visibility === 'true');
        const pricingTableField = adaptionVisible
          ? { acceptPricingTable: [false, Validators.requiredTrue] }
          : {};
        return this.formBuilder.group({
          connectionObject: this.createFormsService.connectionObjectFormWithValidator({}, null),
          connectionObjectValid: [false, Validators.requiredTrue],
          customer: this.formBuilder.array([this.createFormsService.createContactDataFormV2({}, true)]),
          technicalData: this.formBuilder.group({
            meterNumber: ['', [...this.initRequiredValidator(externalImsysInstallationMeterNumberField)]],
            deviceType: [null, [...this.initRequiredValidator(externalImsysInstallationDeviceTypeField)]],
            mobileConnectionStrength: [
              null,
              [...this.initRequiredValidator(externalImsysInstallationMobileConnectionStrengthField)]
            ],
            uploads: this.formBuilder.array<UploadForm>([], {
              validators: [
                ...(externalImsysInstallationUploadsField === FieldConfigEnum.REQUIRED
                  ? [requireDocumentsValidator]
                  : [])
              ]
            }),
            isFeederPresent: [
              null,
              [...this.initRequiredValidator(externalImsysInstallationIsFeederPresentField)]
            ],
            feederType: [
              null,
              [
                ...(this.isFieldRequired(externalImsysInstallationFeederTypeField)
                  ? [this.feederFieldRequiredValidator]
                  : [])
              ]
            ],
            isConsumerPresent: [
              null,
              [...this.initRequiredValidator(externalImsysInstallationIsConsumerPresentField)]
            ],
            consumerType: [
              null,
              [
                ...(this.isFieldRequired(externalImsysInstallationConsumerTypeField)
                  ? [this.consumerFieldRequiredValidator]
                  : [])
              ]
            ],
            isAnnualConsumptionExceeded: [
              null,
              [...this.initRequiredValidator(externalImsysInstallationIsAnnualConsumptionExceededField)]
            ]
          }),
          acceptConditions: [false, Validators.requiredTrue],
          ...pricingTableField
        }) as ImSysCommissioningRequestForm;
      }),
      shareReplay(1)
    );
    this.imSysForm$.subscribe((form) => {
      const technicalDataControls = form.controls.technicalData.controls;
      technicalDataControls.isFeederPresent.valueChanges
        .pipe(startWith(technicalDataControls.isConsumerPresent.value))
        .subscribe(() => {
          technicalDataControls.feederType.updateValueAndValidity();
        });
      technicalDataControls.isConsumerPresent.valueChanges
        .pipe(startWith(technicalDataControls.isConsumerPresent.value))
        .subscribe(() => {
          technicalDataControls.consumerType.updateValueAndValidity();
          technicalDataControls.isAnnualConsumptionExceeded.updateValueAndValidity();
        });
    });
  }

  onGisDataChanged(
    $event: GisDataEvent & { currentCompanyLocationFromGis: CompanyLocationDto },
    validityControl: FormControl<boolean>
  ) {
    this.selectedConnectionObject$.next($event.gisData);
    validityControl.setValue($event.areaAvailable);
  }

  onConfirm(form: ImSysCommissioningRequestForm) {
    const gisData = this.selectedConnectionObject$.getValue();
    if (form.invalid || !gisData) {
      console.log('Form is invalid', form, gisData);
      this.toastrService.error(
        'Bitte beachten Sie die Hinweistexte unter den Formularfeldern und überprüfen Sie Ihre Eingaben.',
        'Das Formular ist ungültig'
      );
      form.markAllAsTouched();
      return;
    }
    const requestModel = new ExternalCommissioningRequestModel(
      ActionTypeEnum.change,
      CommissioningPowerMeterType.iMSys,
      AreaTypeEnum.POWER
    );
    const { customer, technicalData } = form.value;
    const { deviceType, uploads, ...technicalDataRest } = technicalData;
    requestModel.customer = customer[0];
    requestModel.actions[0].existingDevice.deviceType = deviceType?.toString();
    requestModel.actions[0] = {
      ...requestModel.actions[0],
      ...technicalDataRest
    };
    requestModel.uploads = uploads;
    const { houseNo, houseNoAddition, ...rest } = gisData;
    requestModel.companyLocation.external_id = rest.orgEinheit; // orgEinheit wird in companyLocation und connectionObject benötigt
    requestModel.connectionObject = { ...rest, streetNo: houseNo, streetNoAddition: houseNoAddition };

    this.httpHapService
      .postTyped<AnswerWithMoreInfoModel>(requestModel, '/external/commissioningrequest/imsys')
      .subscribe({
        next: (result) => {
          this.toastrService.success('Vielen Dank, wir haben Ihre Daten erhalten.');
          if (result.navigateTo && result.navigateTo !== NavigateToEnum.none) {
            const message = 'Wir haben Ihren Antrag zum Smart-Meter Einbau erhalten.';
            this.router
              .navigate([path.external + '/' + result.navigateTo + '/' + message], {
                replaceUrl: true
              })
              .then();
          }
        },
        error: (err) => {
          this.toastrService.error(
            'Beim Übermitteln ist ein Fehler aufgetreten: ' + err.status + ' ' + err.error
          );
        }
      });
  }

  isUserDecisionYes$(control: FormControl<UserDecisionEnum>): Observable<boolean> {
    return control.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(control.value),
      map((value) => value === UserDecisionEnum.YES)
    );
  }

  isFieldRequired(field: FieldConfigEnum) {
    return field === FieldConfigEnum.REQUIRED;
  }

  isFieldHidden(field: FieldConfigEnum) {
    return field === FieldConfigEnum.HIDDEN;
  }

  initRequiredValidator(field: FieldConfigEnum) {
    return field === FieldConfigEnum.REQUIRED ? [Validators.required] : [];
  }

  feederFieldRequiredValidator(control: FormControl<string>) {
    const parent = control.parent as ImSysCommissioningRequestForm['controls']['technicalData'];
    if (parent && parent.controls.isFeederPresent.value === UserDecisionEnum.YES) {
      return control.value ? null : { required: true };
    }
  }

  consumerFieldRequiredValidator(control: FormControl<string>) {
    const parent = control.parent as ImSysCommissioningRequestForm['controls']['technicalData'];
    if (parent && parent.controls.isConsumerPresent.value === UserDecisionEnum.YES) {
      return control.value ? null : { required: true };
    }
  }

  readonly MobileConnectionStrengthDisplayNames = MobileConnectionStrengthDisplayNames;
  readonly DocumentTagEnum = DocumentTagEnum;
  readonly AreaTypeEnum = AreaTypeEnum;
  protected readonly FieldConfigEnum = FieldConfigEnum;
}
